/**
 * @description 대용량 리스트의 최적화를 위한 가상 스크롤
 * @param items 리스트에 표시할 전체 데이터 배열(Ref)
 * @param itemHeight 긱 헹의 고정 높이(px 단위)
 * @returns 
 * totalHeight: 전체 데이터가 차지하는 총 가상 높이(스크롤바 생성용)
 * visibleItems: 현재 ViewPort에 렌더링해야 할 필터링된 데이터 조각
 * offset: 테이블을 스크롤 위치에 맞게 밀어주는 상단 여백 값(translateY 적용)
 * startIndex: 현재 렌더링 중인 첫 번째 아이템의 원본 배열 인덱스 (순번 표시용)
 * handleScroll: 스크롤 이벤트 발생 시 호출할 핸들러 (바닥 감지 및 위치 갱신)
 * setContainerHeight: 컨테이너의 높이를 수동으로 설정(onMounted시 필수 호출)
 * setScrollTop: 스크롤 위치를 강제 초기화(탭 전환/팝업 오픈 시/화면 전환 시 사용)
 */

export const useVirtualScroll = (items: Ref<any[]>, itemHeight: number) => {
  const scrollTop = ref(0)
  const containerHeight = ref(500)

  // 전체 높이
  const totalHeight = computed(() => items.value.length * itemHeight)

  // 화면에 표시할 개수 자동 계산
  const visibleCount = computed(() => {
    const count = Math.ceil((containerHeight.value || 500) / itemHeight) + 7
    return count
  })

  const startIndex = computed(() => {
    if (scrollTop.value <= 0) return 0

    return Math.floor(scrollTop.value / itemHeight)
  })

  // 렌더링할 아이템
  const visibleItems = computed(() => {
    return items.value.slice(startIndex.value, startIndex.value + visibleCount.value)
  })

  const offset = computed(() => Math.floor(scrollTop.value / itemHeight) * itemHeight)

  /**
   * 
   * @param val 강제로 설정할 스크롤 위치(px)
   */
  const setScrollTop = (val: number) => {
    scrollTop.value = val
  }

  /**
   * 
   * @param event 스크롤 이벤트 객체
   * @param emitEnd 바닥 도달 시 실행할 콜백
   */
  const handleScroll = (event: Event, emitEnd: () => void) => {
    const el = event.target as HTMLElement
    scrollTop.value = el.scrollTop

    // 데이터가 없거나 컨테이너 높이가 0이면 바닥체크 패스
    if(items.value.length == 0 || el.clientHeight == 0) return

    // 스크롤이 바닥 근처에 가면 추가 데이터 로드 함수 실행(약 50px 남았을 때)
    if (el.scrollTop > 0 && el.scrollHeight - el.scrollTop <= el.clientHeight + 50) {
      emitEnd?.()
    }
  }

  /**
   * 
   * @param el 컨테이너(ref.value)
   */
  const setContainerHeight = (el: HTMLElement | null) => {
    if (el) containerHeight.value = el.clientHeight
  }

  /**
   * @description 컨테이너 높이보다 데이터 전체 높이가 커서 스크롤바가 생겼는지 여부
   */
  const hasScroll = computed(() => {
    return totalHeight.value > containerHeight.value
  })

  return { 
    totalHeight, 
    visibleItems, 
    offset, 
    handleScroll,
    setContainerHeight,
    setScrollTop,
    scrollTop,
    startIndex,
    hasScroll
  }
}